/**
 * WEB-3764, юнит «вставка»: одна фабрика нового экрана. Её зовут и действие
 * `addScreen` хранилища редактора («Добавить экран» редактора части), и холст
 * воронки («+» на стрелке, двойной щелчок в рамке части) — второй фабрики нет.
 *
 * Доводка 1 (эталон `editor-c.jsx` `scrTypes`): необязательный вид экрана —
 * заготовка первых блоков из имеющихся. Блок собирается по правилам `addBlock`
 * хранилища: заготовка вида на языке воронки (`buildNewBlockDefaults`) и
 * настройки пакета стиля (`packBlockSettings`). Имя — подпись вида на языке
 * кабинета. В данных экрана вид не хранится (поля вида у экрана нет). Без
 * вида — пустой экран «Экран N» / «Screen N» по языку кабинета (WEB-2438 Ж14).
 * Загрузка — `spinner` (а не `progress_bar`): пауза «готовим ваш план» эталона.
 */
import type { BlockType, EditorBlock, EditorScreen } from '@/store/editorSlice';
import { pickStrings } from '@/i18n';
import { funnelCanvasMapStrings } from '@/i18n/dictionaries/funnelCanvasMap';
import { buildNewBlockDefaults } from './blockDefaults';
import { packBlockSettings } from './packComposition';
import { generateId } from './ids';

/** Виды окна «Вставить экран» — в порядке эталона. */
export const PICKER_SCREEN_KINDS = [
  'choice',
  'info',
  'reviews',
  'email',
  'loader',
  'paywall',
  'blank',
] as const;

/** Вид нового экрана; `welcome` — стартовый экран, в окне его нет. */
export type ScreenKind = (typeof PICKER_SCREEN_KINDS)[number] | 'welcome';

const KIND_BLOCKS: Record<ScreenKind, readonly BlockType[]> = {
  choice: ['title', 'single_choice'],
  info: ['title', 'paragraph', 'image', 'button_next'],
  reviews: ['title', 'testimonials', 'button_next'],
  email: ['title', 'email', 'button_next'],
  loader: ['title', 'spinner'],
  paywall: ['title', 'price_block', 'button_to_pay'],
  blank: [],
  welcome: ['title', 'paragraph', 'button_next'],
};

/** Ключ подписи вида в словаре карты. */
export const SCREEN_KIND_LABEL = {
  choice: 'kindChoice',
  info: 'kindInfo',
  reviews: 'kindReviews',
  email: 'kindEmail',
  loader: 'kindLoader',
  paywall: 'kindPaywall',
  blank: 'kindBlank',
  welcome: 'kindWelcome',
} as const satisfies Record<ScreenKind, string>;

/** Блок по правилам `addBlock`: заготовка на языке воронки и пакет стиля. */
function kindBlock(
  type: BlockType,
  language: string | undefined,
  stylePackId: string | undefined,
): EditorBlock {
  return {
    id: generateId(),
    type,
    content: '',
    ...buildNewBlockDefaults(type, language),
    ...packBlockSettings(stylePackId, type),
  } as EditorBlock;
}

/**
 * Блоки заготовки вида (новые id): то, что положит на экран фабрика. Отдельно —
 * для картинки вида в окне «Новый экран» (WEB-3767): показ окна экран не
 * создаёт, а блоки берутся из того же перечня, второй заготовки нет.
 */
export function kindBlocks(
  kind: ScreenKind,
  language?: string,
  stylePackId?: string,
): EditorBlock[] {
  return KIND_BLOCKS[kind].map((t) => kindBlock(t, language, stylePackId));
}

/**
 * Новый экран с новым id. Без вида — пустой, имя по числу экранов части; с
 * видом — заготовка блоков вида, имя — подпись вида на языке кабинета.
 */
export function newScreen(
  content: readonly EditorScreen[],
  kind?: ScreenKind,
  language?: string,
  stylePackId?: string,
): EditorScreen {
  if (!kind) {
    return {
      id: generateId(),
      // WEB-2438 Ж14 ч.2: «Экран N» / «Screen N» по языку кабинета.
      name: pickStrings(funnelCanvasMapStrings).screenDefaultName(content.length + 1),
      blocks: [],
    };
  }
  return {
    id: generateId(),
    name: pickStrings(funnelCanvasMapStrings)[SCREEN_KIND_LABEL[kind]],
    blocks: kindBlocks(kind, language, stylePackId),
  };
}
